import React, { useContext, useRef } from "react"
import { motion } from "framer-motion"
import { theme } from "../settings/config"
import { PHASES } from "../utils/lessonContent.utils"
import { notebookContext } from "../providers/notebook.provider"
import { exercisePhaseLabel, isMine, isSharedIn, lastUseLabel, ownerLabel } from "../utils/notebook.utils"
import { ARRIVE, SWAP, SchemaCover, swapID } from "./NotebookGallery"
import { SharedBadge } from "./NotebookVariants"

// Gli esercizi del quaderno a lista, raggruppati per fase: l'altro modo di
// guardarli, accanto alla galleria. È quello di chi gli schemi non li
// disegna, che in galleria si troverebbe una fila di campi sfocati; e si
// vede subito quanti esercizi ci sono per il riscaldamento o per la tattica.
//
// Ogni esercizio è una carta col nome, l'inizio del testo e l'ultima volta
// che è servito; la miniatura dello schema, a destra, solo se c'è: lo
// schema sul suo blu, la copertina della galleria in piccolo. Come le carte
// della galleria stanno nel foglio bianco, quindi hanno il fondo caldo.
// Passando alla galleria ogni carta, la miniatura e il nome vanno al loro
// posto là (`swapID`); i titoli delle fasi qui ci sono e là no, e
// compaiono piano.

const c = theme.colors

// il pallino di ogni fase: gli stessi colori delle sagome degli schemi
const PHASE_COLORS = {
	riscaldamento: c.schema_orange,
	tecnica: c.schema_teal,
	tattica: c.schema_violet,
	defaticamento: c.schema_inside,
}

// nell'ordine della lezione; "Qualsiasi fase" in fondo
export const exercisesByPhase = entries =>
	[...PHASES.map(phase => phase.id), null]
		.map(phase => ({ phase, entries: entries.filter(entry => (entry.phase ?? null) === phase) }))
		.filter(group => group.entries.length > 0)

const COVER_RADIUS = [10, 10, 10, 10]

// La voce si apre dalla carta, e dalla miniatura lo schema, se c'è
function PhaseCard({ entry, onOpen }) {
	const { schoolID } = useContext(notebookContext) ?? {}
	const card = useRef(null)
	const cover = useRef(null)
	const where = () => ({
		card: card.current?.getBoundingClientRect(),
		cover: cover.current?.getBoundingClientRect(),
		coverRadius: COVER_RADIUS,
	})
	return (
		<motion.button
			ref={card}
			layoutId={swapID(entry, "card")}
			transition={SWAP}
			type="button"
			whileTap={{ scale: 0.98 }}
			onClick={() => onOpen(where)}
			style={{
				display: "flex",
				alignItems: "center",
				gap: "0.75rem",
				width: "100%",
				padding: "0.625rem 0.75rem 0.625rem 0.875rem",
				border: 0,
				borderRadius: 15,
				// mentre passa dalla galleria la carta cambia forma: quello che
				// non ci sta ancora resta dentro
				overflow: "hidden",
				background: c.background,
				color: c.text,
				font: "inherit",
				textAlign: "left",
				cursor: "pointer",
			}}>
			<motion.span
				layout="position"
				transition={SWAP}
				style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "0.125rem" }}>
				<span style={{ display: "flex", alignItems: "center", gap: "0.375rem", minWidth: 0 }}>
					<motion.span
						layoutId={swapID(entry, "title")}
						layout="position"
						transition={SWAP}
						style={{
							fontWeight: 700,
							fontSize: "0.875rem",
							lineHeight: 1.25,
							color: c.gunmetal,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
						{entry.title}
					</motion.span>
					{/* condiviso: le due sagome accanto al nome, come nelle lezioni */}
					{isMine(entry) && isSharedIn(entry, schoolID) && <SharedBadge entry={entry} />}
				</span>
				{entry.text && (
					<motion.span
						{...ARRIVE}
						style={{
							fontSize: "0.8125rem",
							lineHeight: 1.35,
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
						}}>
						{entry.text}
					</motion.span>
				)}
				<motion.span {...ARRIVE} style={{ fontSize: "0.6875rem", color: c.text_light }}>
					{isMine(entry) ? lastUseLabel(entry) : ownerLabel(entry)}
				</motion.span>
			</motion.span>
			{entry.schema && <SchemaCover ref={cover} entry={entry} radius={COVER_RADIUS} width="4rem" />}
		</motion.button>
	)
}

// `divided`: il filo che le separa dalle pastiglie, se ci sono
function NotebookPhaseList({ entries, onOpen, divided }) {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "0.5rem",
				padding: "0.5rem 0.75rem 0.25rem",
				boxShadow: divided ? `inset 0 1px 0 ${c.gunmetal_lighter}` : "none",
			}}>
			{exercisesByPhase(entries).map(group => (
				<section key={group.phase ?? "any"} aria-label={exercisePhaseLabel(group.phase)}>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={SWAP}
						style={{
							display: "flex",
							alignItems: "baseline",
							justifyContent: "space-between",
							padding: "0.5rem 0.25rem 0.5rem 0.375rem",
						}}>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "0.5rem",
								fontSize: "0.75rem",
								fontWeight: 700,
								letterSpacing: "0.06em",
								textTransform: "uppercase",
								color: c.gunmetal,
							}}>
							<span
								aria-hidden="true"
								style={{
									width: "0.5rem",
									height: "0.5rem",
									borderRadius: "50%",
									background: PHASE_COLORS[group.phase] ?? c.text_light,
								}}
							/>
							{exercisePhaseLabel(group.phase)}
						</span>
						<span style={{ fontSize: "0.75rem", color: c.text_light }}>
							{group.entries.length === 1 ? "1 esercizio" : `${group.entries.length} esercizi`}
						</span>
					</motion.div>
					<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
						{group.entries.map(entry => (
							<PhaseCard key={entry.entryID} entry={entry} onOpen={where => onOpen(entry.entryID, where)} />
						))}
					</div>
				</section>
			))}
		</div>
	)
}

export default NotebookPhaseList
